/**
 * CatalogTree — the shared Product → Brand → Model directory sidebar + two-pane layout used
 * by the Mockups Select tab and both Templates surfaces. PRESENTATIONAL: it takes the already-
 * loaded `templates` + `phoneModels` as props (each surface owns its own fetching/caching) and
 * renders its right pane via a render-prop that receives the currently-selected ModelNode
 * (or null before a model is picked). Owns only tree state: expansion, selection, search.
 *
 * NOTE ON THE FILE NAME: the task brief that specced this component named this file
 * "CatalogTree.tsx", sitting next to Task 1's pure derivation module "catalogTree.ts" —
 * names differing only by the case of their first letter. That pairing is unbuildable:
 * `tsc` (forceConsistentCasingInFileNames) hard-errors with TS1149 the moment both files
 * are in the program, and on case-insensitive/preserving filesystems (default macOS APFS,
 * default Windows NTFS) any extension-probed import specifier for "CatalogTree" silently
 * resolves to catalogTree.ts's exports instead (verified directly via `fs.existsSync`,
 * independent of any bundler cache). Renamed to "CatalogTreeView.tsx" to avoid the
 * collision; the exported symbols below (`CatalogTree`, `CatalogTreeProps`) are unchanged
 * from the brief so only the import path differs for consumers.
 */
import { useMemo, useRef, useState } from "react";
import { EmptyState } from "../../ui/EmptyState.js";
import type { PhoneModelOption, ProductTemplate } from "@genz/platform";
import {
  buildCatalogTree,
  filterCatalogTree,
  type BrandNode,
  type ModelNode,
  type ProductNode,
} from "./catalogTree.js";
import "./catalog.css";
import { Icon } from "../../ui/Icon.js";

export interface CatalogTreeProps {
  templates: ProductTemplate[];
  phoneModels: PhoneModelOption[];
  /** Re-fetch hook wired by each surface to its own loader. */
  onReload?: () => void;
  /** Sidebar empty-state text when the tree is empty. */
  emptyLabel?: string;
  /** Right pane: called with the selected model, or null before selection. */
  children: (selected: ModelNode | null) => React.ReactNode;
  /**
   * Controlled search: when provided, the tree filters by THIS query and renders no
   * search input of its own — the page's single search box drives the whole surface
   * (one search per page; the third input was the "everything is cluttered" report's
   * biggest offender). Omit it to keep the tree's own input (legacy surfaces).
   */
  query?: string;
  /**
   * The breadcrumb when NOTHING is selected, given the number of templates on screen
   * (WS-B B-6 / F-16). The bare "Select a model" sat above a fully populated table — it
   * described a control, not the content, and the pane it labelled was already showing every
   * row. A caller that names what is on screen ("All models · 12 templates") gets that;
   * a caller that omits this keeps today's copy, so the Mockup Generator — where nothing IS
   * shown until a model is picked, and the instruction is therefore correct — is unaffected.
   */
  allLabel?: (count: number) => string;
}

export function CatalogTree({ templates, phoneModels, onReload, emptyLabel, children, query: controlledQuery, allLabel }: CatalogTreeProps) {
  const [ownQuery, setOwnQuery] = useState("");
  const controlled = controlledQuery !== undefined;
  const query = controlled ? controlledQuery : ownQuery;
  const [openProducts, setOpenProducts] = useState<Set<string>>(new Set());
  const [openBrands, setOpenBrands] = useState<Set<string>>(new Set());
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const tree = useMemo(() => buildCatalogTree(templates, phoneModels), [templates, phoneModels]);
  const view = useMemo(() => filterCatalogTree(tree, query), [tree, query]);

  // A model's SELECTION key is namespaced by its tree position (product::brand::modelKey),
  // NOT the raw ModelNode.key (a phone-model id), because the same phone model legitimately
  // appears under multiple product types (e.g. "iPhone 15" with both a clear_case and a
  // phone_case template → two distinct ModelNodes sharing key "ip15"). Using the raw key
  // would make a flat lookup return the FIRST match — wrong templates/breadcrumb, and both
  // rows highlighting. ModelNode.key itself stays untouched (Task 1's contract).
  const modelSelKey = (p: ProductNode, b: BrandNode, m: ModelNode) => `${p.key}::${b.label}::${m.key}`;

  /**
   * The node for `selectedKey`, remembered across a filter that temporarily empties its
   * branch (WS-B B-6).
   *
   * The tree is now built from the page's ONE derived row set (F-08: its counts have to
   * agree with the table's), so a status chip matching zero rows empties the tree — and a
   * `selected` derived purely from `tree` would silently drop back to "nothing selected",
   * losing the drilled-in model and the breadcrumb that names it. Holding the last resolved
   * node keeps the heading honest ("Clear Case / Apple / iPhone 15") while the pane below it
   * correctly shows "no rows match". A real deselect clears `selectedKey`, which clears this
   * too, so nothing is retained that the user did not choose.
   */
  const lastSelected = useRef<{ key: string; node: ModelNode } | null>(null);
  const selected: ModelNode | null = useMemo(() => {
    if (!selectedKey) {
      lastSelected.current = null;
      return null;
    }
    for (const p of tree) for (const b of p.brands) for (const m of b.models) {
      if (modelSelKey(p, b, m) === selectedKey) {
        lastSelected.current = { key: selectedKey, node: m };
        return m;
      }
    }
    return lastSelected.current?.key === selectedKey ? lastSelected.current.node : null;
  }, [tree, selectedKey]);

  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, key: string) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key); else next.add(key);
    setter(next);
  };
  // A brand key is namespaced by product so identically-named brands under different
  // products expand independently.
  const brandKey = (p: ProductNode, brandLabel: string) => `${p.key}::${brandLabel}`;

  return (
    <div className="catalog" data-testid="catalog-tree">
      <aside className="catalog__side">
        {!controlled && (
          <input
            className="catalog__search"
            placeholder="Search product / brand / model"
            value={ownQuery}
            onChange={(e) => setOwnQuery(e.target.value)}
            data-testid="catalog-search"
            aria-label="Search catalog"
          />
        )}
        {view.length === 0 ? (
          query.trim() === "" ? (
            <EmptyState icon="grid" size="sm" title={emptyLabel ?? "No products yet"} testId="catalog-empty" />
          ) : (
            <EmptyState icon="search" size="sm" title="No matches." testId="catalog-no-match" />
          )
        ) : (
          <ul className="catalog__list" role="tree">
            {view.map((p) => {
              const pOpen = openProducts.has(p.key) || query.trim() !== "";
              return (
                <li key={p.key} className="catalog__node" role="treeitem" aria-expanded={pOpen}>
                  <button
                    type="button"
                    className="catalog__row catalog__row--product"
                    onClick={() => toggle(openProducts, setOpenProducts, p.key)}
                    data-testid={`catalog-product-${p.key}`}
                  >
                    <span className="catalog__twist">{pOpen ? <Icon name="chevron-down" size={16} /> : <Icon name="chevron-right" size={16} />}</span>
                    <span className="catalog__label">{p.label}</span>
                    <span className="catalog__count">{p.count}</span>
                  </button>
                  {pOpen && (
                    <ul className="catalog__list" role="group">
                      {p.brands.map((b) => {
                        const bk = brandKey(p, b.label);
                        const bOpen = openBrands.has(bk) || query.trim() !== "";
                        return (
                          <li key={bk} className="catalog__node" role="treeitem" aria-expanded={bOpen}>
                            <button
                              type="button"
                              className="catalog__row catalog__row--brand"
                              onClick={() => toggle(openBrands, setOpenBrands, bk)}
                              data-testid={`catalog-brand-${bk}`}
                            >
                              <span className="catalog__twist">{bOpen ? <Icon name="chevron-down" size={16} /> : <Icon name="chevron-right" size={16} />}</span>
                              <span className="catalog__label">{b.label}</span>
                              <span className="catalog__count">{b.count}</span>
                            </button>
                            {bOpen && (
                              <ul className="catalog__list" role="group">
                                {b.models.map((m) => {
                                  const selKey = modelSelKey(p, b, m);
                                  const on = selectedKey === selKey;
                                  return (
                                    <li key={m.key} className="catalog__node" role="treeitem" aria-selected={on}>
                                      <button
                                        type="button"
                                        className={`catalog__row catalog__row--model${on ? " catalog__row--on" : ""}`}
                                        onClick={() => setSelectedKey(selKey)}
                                        data-testid={`catalog-model-${m.key}`}
                                      >
                                        <span className="catalog__label">{m.label}</span>
                                        <span className="catalog__count">{m.count}</span>
                                      </button>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {onReload && (
          <button type="button" className="btn btn--sm btn--ghost catalog__reload"
                  onClick={onReload} data-testid="catalog-reload"><Icon name="refresh" size={16} /> Refresh</button>
        )}
      </aside>

      <section className="catalog__main">
        <div className="catalog__crumb" data-testid="catalog-breadcrumb">
          {selected
            ? `${selected.product} / ${selected.brand} / ${selected.label}`
            : allLabel?.(view.reduce((n, p) => n + p.count, 0)) ?? "Select a model"}
        </div>
        <div className="catalog__pane">{children(selected)}</div>
      </section>
    </div>
  );
}
